import type { ReactNode } from "react";
import { PREVIEW } from "../lib/preview";
import { HowThisWorks } from "../modules/help/help-links";

/** Per-screen top bar: eyebrow, title, optional meta and actions, and the screen's "How this works" link. */
export function PageHeader({ eyebrow, title, meta, actions, help = true }: { eyebrow: string; title: string; meta?: ReactNode; actions?: ReactNode; help?: boolean }) {
  const guide = help ? <HowThisWorks /> : null;
  return (
    <header className="page-head">
      <div className="page-head__text">
        <p className="eyebrow">{eyebrow}</p>
        <div className="page-head__title">
          <h1 id="page-title" tabIndex={-1}>{title}</h1>
          {PREVIEW && <span className="tag" title="DEV preview mode: fixture data, not the database">Preview data</span>}
        </div>
        {meta && <p className="page-head__meta">{meta}</p>}
      </div>
      {(actions || guide) && <div className="page-head__actions">{guide}{actions}</div>}
    </header>
  );
}

/** Search and filter row above a table. */
export function FilterBar({ children, count }: { children: ReactNode; count?: string }) {
  return (
    <div className="filters" role="search">
      {children}
      {count && <p className="filters__count" aria-live="polite">{count}</p>}
    </div>
  );
}

export function SearchField({ value, onChange, label, placeholder }: { value: string; onChange: (v: string) => void; label: string; placeholder?: string }) {
  return (
    <label className="field field--search">
      <span className="sr-only">{label}</span>
      <svg className="field__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6" /><path d="M15 15l5.5 5.5" /></svg>
      <input className="input" type="search" value={value} placeholder={placeholder ?? label} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

export function SelectField({ value, onChange, label, options }: { value: string; onChange: (v: string) => void; label: string; options: { value: string; label: string }[] }) {
  return (
    <label className="field field--select">
      <span className="field__label">{label}</span>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
